import { Link } from "react-router-dom";
import {
  Radar, Stethoscope, Fingerprint, Mail, Globe, Bug, BookOpen, CheckSquare,
  Bot, Sparkles, Zap, Clock, Users as UsersIcon, KeyRound, Plug, Satellite,
  Terminal, ShieldAlert, Search, FileSignature, History, Share2, ShieldCheck,
  Sliders, ChevronRight, Printer, ArrowRight, Cpu, ListChecks, AlertOctagon,
  Lock, Wrench,
} from "lucide-react";

/**
 * Full user guide — authenticated help centre.
 *
 * Layout: fixed TOC on the left · rich content on the right ·
 * print-friendly (each section stacks on the printed page).
 */
export default function UserGuide() {
  const sections = [
    { id: "quick-start",    title: "Quick start",              icon: Radar },
    { id: "rca",            title: "Root Cause Finder AI",     icon: Stethoscope, badge: "NEW" },
    { id: "investigations", title: "Investigations",           icon: Fingerprint },
    { id: "email",          title: "Email Investigator",       icon: Mail },
    { id: "threat-map",     title: "Threat Map · Live",        icon: Globe },
    { id: "cve",            title: "CVE Tracker + Health",     icon: Bug },
    { id: "playbooks",      title: "Playbooks & Connectors",   icon: BookOpen },
    { id: "compliance",     title: "Compliance",               icon: CheckSquare },
    { id: "ai",             title: "AI: Hunter · Ask · Copilot", icon: Bot },
    { id: "automation",     title: "Automation & Briefings",   icon: Zap },
    { id: "users",          title: "Users, MFA & Break-glass", icon: UsersIcon },
    { id: "sidebar",        title: "Customize Sidebar",        icon: Sliders,     badge: "NEW" },
    { id: "runbook",        title: "Signed PDF Runbook",       icon: FileSignature, badge: "NEW" },
    { id: "api",            title: "API keys & integrations",  icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-[#060606] text-white print:bg-white print:text-black">
      {/* Print stylesheet */}
      <style>{`
        @media print {
          .toc, .no-print { display: none !important; }
          .guide-body { padding: 0 !important; max-width: 100% !important; }
          section { page-break-inside: avoid; }
          h1, h2, h3 { color: #000 !important; }
          .brand-dark { background: #fff !important; color: #000 !important; }
          a { color: #005B7A !important; text-decoration: underline; }
          pre { background: #f4f4f4 !important; color: #000 !important; border: 1px solid #ddd; }
        }
      `}</style>

      <div className="max-w-[1400px] mx-auto flex gap-8 p-8">
        {/* TOC — sticky */}
        <aside className="toc w-64 shrink-0 sticky top-8 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-3 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Contents
          </div>
          <nav className="space-y-0.5">
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs text-[#8E9196] hover:text-white hover:bg-white/5 transition-colors">
                <s.icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                <span className="flex-1 truncate">{s.title}</span>
                {s.badge && (
                  <span className="font-mono text-[8px] uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded-sm px-1">
                    {s.badge}
                  </span>
                )}
              </a>
            ))}
          </nav>
          <div className="mt-6 border-t border-white/10 pt-4">
            <button onClick={() => window.print()}
              className="w-full inline-flex items-center justify-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-[10px] tracking-widest">
              <Printer className="w-3 h-3" /> Print · save PDF
            </button>
            <Link to="/quick-view"
              className="w-full mt-2 inline-flex items-center justify-center gap-1.5 border border-white/15 hover:border-[#B57BFF] hover:text-[#B57BFF] text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-[10px] tracking-widest">
              Public quick view
            </Link>
          </div>
          <div className="mt-4 text-[9px] font-mono uppercase tracking-widest text-[#52525B] text-center">
            Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
          </div>
        </aside>

        {/* Content */}
        <article className="guide-body flex-1 min-w-0 max-w-[900px]">
          <header className="mb-10">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] flex items-center gap-2">
              <Radar className="w-3.5 h-3.5" /> ForensiQ · Handbook · v6.5
            </div>
            <h1 className="font-display font-black text-6xl mt-3 leading-none">
              The <span className="text-[#00F0FF]">Investigation-first</span><br/>SOC handbook.
            </h1>
            <p className="text-[#8E9196] text-lg mt-4 max-w-2xl">
              Everything ForensiQ can do — SOC log analysis, email forensics,
              live threat mapping, CVE tracking, AI-driven root-cause diagnosis,
              signed incident runbooks, and the little touches that make a SOC
              team calmer at 3 AM.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Multi-tenant SaaS", "Agent-less RCA", "HMAC-signed runbooks", "Guided fix runner", "Live threat feeds"].map((t) => (
                <span key={t} className="font-mono text-[10px] uppercase tracking-widest text-[#8E9196] border border-white/15 rounded-sm px-2 py-0.5">
                  {t}
                </span>
              ))}
            </div>
          </header>

          {/* -------------------------------- Quick start -------------------------------- */}
          <Section id="quick-start" icon={Radar} title="Quick start · get value in 5 minutes">
            <p>
              ForensiQ is designed so a first-time analyst can trace a P1 incident from
              the first minute of the shift. Here&apos;s the 5-minute path:
            </p>
            <Steps items={[
              { title: "Sign in", body: "Use your tenant credentials. New tenants get a bootstrap admin at `admin@<slug>.io` — the seed password lands in your welcome email." },
              { title: "Land on Mission Control", body: "The dashboard shows the last-24h alert count, open incidents, threat score and a rolling MTTR estimate. The recent-incidents strip lets you jump into any case." },
              { title: "Diagnose an incident with Root Cause Finder AI", body: "Paste a plain-English description ('CrowdStrike agent stopped reporting since 3am') into the AI. You get a sensor probe, aspect-by-aspect checks and ranked hypotheses in ~30s." },
              { title: "Attach a signed runbook to the ticket", body: "One click on ‘Runbook’ downloads a ~24KB HMAC-signed PDF. Drop it on your ITSM ticket." },
              { title: "Share with a teammate", body: "The Share button copies a tenant-gated URL — the recipient signs in and sees the same session (read-only) with no re-work." },
            ]} />
          </Section>

          {/* -------------------------------- RCA -------------------------------- */}
          <Section id="rca" icon={Stethoscope} title="Root Cause Finder AI" badge="NEW">
            <p>
              A senior SRE + SecOps engineer that behaves like an <span className="text-[#00F0FF]">agent-less sensor</span>.
              Describe any P1 (endpoint agent, application, network, DNS, TLS, service…) and the AI
              returns a full sensor report — no probes, no shell access, no software on the target.
            </p>
            <FeatureGrid items={[
              { icon: Cpu,           title: "Sensor probe",           body: "Chip list of scanned aspects (network, DNS, port 443, service state, agent integrity…) and a confidence badge." },
              { icon: Stethoscope,   title: "Aspect-by-aspect checks", body: "6–10 concrete checks per incident (Internet ✓ · port 443 blocked · agent corrupted · DNS resolves) with copy-pasteable verify commands." },
              { icon: AlertOctagon,  title: "Ranked hypotheses",      body: "Each with confidence %, likelihood, investigate + fix commands ready to paste into PowerShell / bash." },
              { icon: ListChecks,    title: "Guided fix runner",      body: "Tick every check + command as Confirmed OK / BAD / Skip with notes + raw output. Recompute recalculates the verdict live." },
              { icon: History,       title: "History drawer",         body: "The last 20 sessions per user; click to reload; delete individually; auto-evicts oldest on 21st." },
              { icon: Share2,        title: "Tenant-scoped share",    body: "One click copies a login-gated URL that any teammate in your tenant can open (read-only)." },
              { icon: FileSignature, title: "Signed PDF runbook",     body: "One-click download of a US-Letter PDF with verdict banner, sensor probe, all commands + a QR-verify signature block." },
              { icon: ShieldCheck,   title: "Public verification",    body: "GET /api/rca/verify?sid=&sig= — anyone with the printed URL can confirm AUTHENTIC / TAMPERED without logging in." },
            ]} />
            <SubHead>How to use it</SubHead>
            <Steps items={[
              { title: "Open Root Cause Finder AI", body: "First tab in your sidebar (labelled `AI`). URL: /rca" },
              { title: "Describe the incident", body: "Plain English, one paragraph. Include OS, service, symptom, when it started, any recent changes. The more context, the sharper the hypotheses." },
              { title: "Read the sensor report", body: "You'll see: verdict card (red/amber/green + headline), sensor probe strip, system profile (inferred), recent changes to check, aspect-by-aspect checklist, ranked hypotheses, next actions." },
              { title: "Run the checks", body: "Copy any verify_cmd on the target machine. As results come back, click the OK / BAD / Skip pill on each row and paste the raw output." },
              { title: "Recompute", body: "The sticky footer pill shows how many findings are pending. One click recomputes — verdict typically escalates from 78% MOST LIKELY to 92% ROOT CAUSE CONFIRMED." },
              { title: "Export or share", body: "‘Runbook’ downloads a signed PDF · ‘Share’ copies a tenant-gated link · ‘MD’ exports Markdown · ‘New’ starts a fresh session." },
            ]} />
            <Callout tone="cyan" title="Why 'agent-less'?">
              The AI never touches your machine. It reasons about the described system the way an
              experienced engineer would produce a diagnosis from a paged incident report — pattern-matching
              symptom → most-likely-cause, then giving you exact commands to verify.
            </Callout>
          </Section>

          {/* -------------------------------- Investigations -------------------------------- */}
          <Section id="investigations" icon={Fingerprint} title="Investigations">
            <p>
              The case management workspace. Each investigation is a container for logs, IOCs,
              suspects, timeline events, notes and outputs from any AI / playbook run against it.
            </p>
            <FeatureGrid items={[
              { icon: Fingerprint, title: "Case Detail",    body: "Timeline, IOC list, related logs, attack story graph, notes, tags." },
              { icon: Bot,         title: "AI Summarize",   body: "Ask ForensiQ to summarize the case, extract IOCs, or draft the executive brief." },
              { icon: ShieldAlert, title: "Escalate",       body: "Turn any investigation into an Incident with severity + owner." },
            ]} />
          </Section>

          {/* -------------------------------- Email Investigator -------------------------------- */}
          <Section id="email" icon={Mail} title="Email Investigator">
            <p>
              A dedicated Gmail/IMAP inbox becomes ForensiQ&apos;s investigation intake.
              Send an email to the ForensiQ mailbox with a specific subject convention,
              and the platform automatically opens a case, extracts IOCs, generates a
              PDF report, and replies back — all threaded in one conversation.
            </p>
            <Callout tone="cyan" title="The trigger convention — how to submit an incident">
              <p className="mb-2">Your subject line must either <b>contain the marker</b> <span className="font-mono text-white">[INVESTIGATE]</span> (anywhere, any case) OR <b>start with the word</b> <span className="font-mono text-white">Investigate</span>. Both styles are accepted so casual and formal senders both work.</p>
              <pre className="text-[11px] bg-black/40 border border-white/5 rounded-sm p-2 overflow-x-auto text-white/85 font-mono not-prose">
{`To:      forensiq.analysis@gmail.com
Subject: [INVESTIGATE] Suspicious login from Chennai office
   — or —
Subject: Investigate suspicious login from Chennai office
Body:    User bob@acme.io logged in from 45.33.32.156 at 03:14 UTC.`}
              </pre>
              <p className="mt-2 text-xs">Mails where the word "investigate" appears <b>mid-sentence</b> (e.g. <i>"Please investigate this refund"</i>) are still silently ignored — so casual customer-service threads never accidentally open a case.</p>
            </Callout>
            <SubHead>What happens next</SubHead>
            <Steps items={[
              { title: "Acknowledgment (~5 seconds)", body: "You receive a branded ack email with subject [ForensiQ FQ-2026-0247] Acknowledgment — Investigation Started. This gives you the case number immediately so you can reference it internally." },
              { title: "Full report (~60 seconds)", body: "A second email arrives with subject [ForensiQ FQ-2026-0247] Investigation Report: <your subject>. Attached is a PDF report containing IOC enrichment, CVE cross-references, hypothesis ranking and remediation steps." },
              { title: "Reply on the thread to add context (NO new report)", body: "Keep the [ForensiQ FQ-2026-0247] tag in your subject line and just hit reply. Your message is attached to the SAME case as a customer_replies entry — no new case is opened and NO new investigation report is generated. You'll get a short 'Update received' confirmation instead." },
              { title: "Live case view", body: "Every incoming email shows up in the Email Investigator page's right pane — click any row to open the full case with the timeline, IOCs, hypotheses and PDF report." },
            ]} />
            <SubHead>Fetch controls (built-in — no config needed)</SubHead>
            <FeatureGrid items={[
              { icon: Mail,        title: "Subject trigger", body: "Either [INVESTIGATE] anywhere in the subject OR the word 'Investigate' as the first word. Anything else is dropped in <1ms — no LLM calls, no PDF, no reply." },
              { icon: Clock,       title: "Recent-only window", body: "The poller only fetches mail from the LAST 24 HOURS. Old inbox history is skipped — no risk of replaying last month's mail on a config change." },
              { icon: ShieldCheck, title: "TO field required", body: "The ForensiQ mailbox address MUST be in the TO field (not CC or BCC). Prevents group threads from accidentally opening cases." },
              { icon: Lock,        title: "Allowlist",       body: "Optional allowed_senders (e.g. *@yourcompany.com, ah.deen@outlook.com). Non-listed senders are dropped even with the [INVESTIGATE] tag." },
              { icon: Fingerprint, title: "Magic-link verification", body: "First-time senders receive a one-time magic link. Prevents spoofed FROM addresses from opening cases." },
              { icon: Fingerprint, title: "One-and-done lock", body: "Each email's Message-ID is claimed the instant we accept it, BEFORE the AI runs. If the pipeline ever crashes, the next poll still skips it — no duplicate acks, no duplicate reports, no spam." },
              { icon: Stethoscope, title: "Diagnostics panel", body: "The Live-Inbox view shows a live IMAP+SMTP health check, poller telemetry (last success / last error), recent processed messages, and a Poll Now button." },
            ]} />
            <SubHead>Setup (one time)</SubHead>
            <Steps items={[
              { title: "Provision a dedicated Gmail (or Google Workspace) inbox", body: "e.g. forensiq.analysis@yourcompany.com. Never use a shared human inbox — ForensiQ marks messages as seen." },
              { title: "Enable 2-Step Verification + generate an App Password", body: "myaccount.google.com → Security → App passwords. Paste the 16-character password into the IMAP + SMTP fields — Gmail no longer accepts regular account passwords via IMAP." },
              { title: "Configure IMAP/SMTP in ForensiQ", body: "Email Investigator → Configure. IMAP: imap.gmail.com:993. SMTP: smtp.gmail.com:587. Username = your mailbox, password = App Password." },
              { title: "(Optional) Add allowed senders", body: "*@yourcompany.com blocks strangers even with the [INVESTIGATE] tag. Also add specific outside partner emails like partner@vendor.io." },
              { title: "Test with your own email", body: "Send yourself a mail with subject [INVESTIGATE] test + a fake IP in the body. Ack should arrive in seconds, full report within ~60s." },
            ]} />
          </Section>

          {/* -------------------------------- Threat Map -------------------------------- */}
          <Section id="threat-map" icon={Globe} title="Threat Map · Live">
            <p>
              A 2D and 3D world map of live threats streamed from the free intel feeds
              (Feodo, Blocklist.de, URLhaus, Emerging Threats, Spamhaus, Tor exit nodes)
              and CIRCL. Rotate the globe, hover for details, click to hunt.
            </p>
          </Section>

          {/* -------------------------------- CVE Tracker -------------------------------- */}
          <Section id="cve" icon={Bug} title="CVE Tracker + egress diagnostic">
            <p>
              Live CVE feed pulled from CIRCL, NVD and CISA KEV. Platform breakdown groups
              disclosures by Windows / Linux / macOS / AWS / Azure / GCP so the on-call
              knows exactly what to patch first.
            </p>
            <FeatureGrid items={[
              { icon: Search,       title: "Live ticker",           body: "10-minute cache, force-refresh from the header, filter by product, CVSS or CISA-KEV." },
              { icon: ShieldAlert,  title: "Platform breakdown",    body: "Sunburst grouped by OS / cloud / infra so you can spot the noisiest surface for your fleet." },
              { icon: ShieldCheck,  title: "Egress diagnostic",     body: "GET /api/cve-tracker/health returns up/down status per upstream feed. Use it in prod to prove the corporate firewall is the reason the tracker is empty." },
            ]} />
            <Callout tone="amber" title="Prod says ‘no data’?">
              Hit <span className="font-mono text-white">/api/cve-tracker/health</span> from the prod URL.
              If any source returns <span className="font-mono text-white">connect_error</span> or
              <span className="font-mono text-white"> connect_timeout</span>, your egress firewall is blocking
              outbound HTTPS to that upstream. Allow-list it.
            </Callout>
          </Section>

          {/* -------------------------------- Playbooks -------------------------------- */}
          <Section id="playbooks" icon={BookOpen} title="Playbooks & Connectors">
            <p>
              Author, schedule and execute SOAR playbooks. Each playbook is a set of steps
              chained across your connectors — Slack, Splunk, Sentinel, EDR, ticketing, etc.
            </p>
            <FeatureGrid items={[
              { icon: BookOpen, title: "Playbook editor",  body: "Drag steps in order, template each with Jinja-style variables from the incident context." },
              { icon: Plug,     title: "Connectors",       body: "Save credentials once; every playbook step picks them up. Rotate keys from Admin → API Keys." },
              { icon: Zap,      title: "Trigger",          body: "Manual run, cron schedule, or webhook from an alert (Sentinel/Splunk/Datadog)." },
            ]} />
          </Section>

          {/* -------------------------------- Compliance -------------------------------- */}
          <Section id="compliance" icon={CheckSquare} title="Compliance dashboards">
            <p>
              Per-tenant compliance frameworks — NIST CSF, ISO 27001, PCI-DSS, HIPAA, SOC 2.
              Each control has an owner, evidence, status, and a scheduled re-check.
            </p>
          </Section>

          {/* -------------------------------- AI -------------------------------- */}
          <Section id="ai" icon={Bot} title="AI: Hunter · Ask · Copilot">
            <p>
              Three AI surfaces, all powered by Claude / GPT / Gemini via a universal
              key so you never rotate provider keys.
            </p>
            <FeatureGrid items={[
              { icon: Bot,      title: "Threat Hunting AI", body: "Guided hunts — pick a hypothesis, the AI writes the queries, executes across connected data lakes, and returns triaged findings." },
              { icon: Sparkles, title: "Ask ForensiQ",      body: "Natural-language Q&A over your tenant's logs, cases, and knowledge base." },
              { icon: Bot,      title: "SOC Copilot",       body: "Persistent chat pinned to any investigation, with tool-use for lookups, enrichment and runbook drafting." },
            ]} />
          </Section>

          {/* -------------------------------- Automation & Briefings -------------------------------- */}
          <Section id="automation" icon={Zap} title="Automation & Shift Briefings">
            <p>
              Every 8h, ForensiQ compiles a briefing for the incoming shift — open incidents,
              hot IOCs, KEV additions in the last 24h, expiring playbook cron jobs, MTTR delta.
            </p>
            <FeatureGrid items={[
              { icon: Clock, title: "Shift briefings",     body: "8H cadence · delivered to your Slack digest channel · always printable as a PDF." },
              { icon: Zap,   title: "Automation engine",   body: "Time-triggered + event-triggered playbooks with dead-letter handling and per-run audit." },
            ]} />
          </Section>

          {/* -------------------------------- Users & MFA -------------------------------- */}
          <Section id="users" icon={UsersIcon} title="Users, MFA & Break-glass">
            <p>
              Multi-tenant RBAC with granular permissions (per-user + role-based).
              MFA is opt-in — including for super-admins — and a break-glass
              flow keeps you unblocked when authenticator devices go missing.
            </p>
            <FeatureGrid items={[
              { icon: UsersIcon,   title: "Roles",             body: "admin, analyst, viewer, plus per-user permission grant / deny lists for one-off overrides." },
              { icon: Lock,        title: "MFA opt-in",        body: "TOTP + 10 single-use recovery codes. Super-admins are NOT forced into MFA on first login — they choose." },
              { icon: Wrench,      title: "Force-disable MFA", body: "Any tenant admin (users.manage) can force-disable MFA on a user in their tenant. Bumps token_version → invalidates every stale session & MFA challenge. Frozen founder account is protected." },
              { icon: ShieldCheck, title: "Frozen founder",    body: "The platform founder account (ah.deen@gmail.com) cannot be demoted, deleted, or force-disabled — must self-manage from their profile." },
            ]} />
            <Callout tone="cyan" title="Someone locked out of MFA?">
              <ol className="list-decimal ml-5 space-y-1">
                <li>Any admin: <span className="font-mono">Admin → Users</span></li>
                <li>Find the user → click the <span className="font-mono">ShieldOff</span> icon at the far right</li>
                <li>Confirm the break-glass warning</li>
                <li>Tell the user to log in fresh with just their password. Any old tab / MFA challenge on their side is now invalidated.</li>
              </ol>
            </Callout>
          </Section>

          {/* -------------------------------- Sidebar customize -------------------------------- */}
          <Section id="sidebar" icon={Sliders} title="Customize your sidebar" badge="NEW">
            <p>
              Reorder any tab in your sidebar, or hide the ones you never use. Two ways:
            </p>
            <FeatureGrid items={[
              { icon: Sliders,   title: "In-place customize", body: "Click the Sliders icon at the top-right of the sidebar. Every tab gets a drag handle + eye toggle. Drag rows within a section, click the eye to hide, tap ✓ to save. Cancel discards." },
              { icon: BookOpen,  title: "Full settings page", body: "Admin → Customize Sidebar. Same drag/hide, plus ‘Save as tenant default’ (admin-only) — every teammate in your tenant who hasn't customized picks it up on their next load." },
              { icon: UsersIcon, title: "Per-user + per-tenant", body: "Your override always beats the tenant default; the tenant default beats the factory order. Newly-released tabs auto-appear at the end of their section — never miss a feature." },
            ]} />
          </Section>

          {/* -------------------------------- Signed Runbook -------------------------------- */}
          <Section id="runbook" icon={FileSignature} title="Signed PDF Runbook" badge="NEW">
            <p>
              Any Root Cause Finder AI session can be exported as a
              <span className="text-[#00F0FF]"> HMAC-SHA256 sealed PDF</span>. Attach it to the incident
              ticket; the QR code on the last page links to a public verify endpoint that
              returns AUTHENTIC / TAMPERED for anyone reviewing the ticket.
            </p>
            <Steps items={[
              { title: "Diagnose the incident in RCA", body: "Verdict + sensor probe + checks + hypotheses." },
              { title: "(Optional) Recompute with your ticks", body: "Get the escalated verdict — the PDF captures the LATEST state." },
              { title: "Click Runbook", body: "One click. A ~24KB `ForensiQ-Runbook-<sid>.pdf` downloads with signature header + footer on every page." },
              { title: "Attach to the ticket", body: "Anyone reviewing can scan the QR code (or hit the printed URL) to verify authenticity." },
            ]} />
          </Section>

          {/* -------------------------------- API -------------------------------- */}
          <Section id="api" icon={KeyRound} title="API keys & integrations">
            <p>
              Programmatic access is via bearer keys minted under Admin → API Keys.
              Each key is scoped to a set of permissions and rotated / revoked without
              downtime.
            </p>
            <FeatureGrid items={[
              { icon: KeyRound, title: "Minted per scope", body: "Grant only the permissions the caller needs (e.g. `logs.ingest`)." },
              { icon: Terminal, title: "Ingest endpoint",  body: "POST /api/logs/ingest — JSON, CEF or syslog. Sample rate-limited per key." },
              { icon: Satellite, title: "Threat intel API", body: "GET /api/threat-intel/lookup — IOC → verdict + provenance in <1s cache-hit." },
            ]} />
          </Section>

          {/* Footer */}
          <footer className="mt-14 border-t border-white/10 pt-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
              Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span> · ForensiQ Handbook v6.5 · Feb 2026
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
}

/* --------------------------- Presentational bits --------------------------- */

function Section({ id, icon: Icon, title, badge, children }) {
  return (
    <section id={id} className="mb-14 scroll-mt-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 border border-[#00F0FF]/40 rounded-sm flex items-center justify-center bg-[#00F0FF]/[0.04]">
          <Icon className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="font-display font-black text-3xl">{title}</h2>
          {badge && (
            <span className="font-mono text-[9px] uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded-sm px-1.5">
              {badge}
            </span>
          )}
        </div>
      </div>
      <div className="prose prose-invert max-w-none text-sm text-[#C9CBCE] leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function SubHead({ children }) {
  return (
    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mt-6 mb-3 flex items-center gap-1.5">
      <ChevronRight className="w-3 h-3 text-[#00F0FF]" /> {children}
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="space-y-2 not-prose">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 border-l-2 border-[#00F0FF]/30 pl-4 py-1">
          <span className="font-mono text-[10px] text-[#00F0FF] mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
          <div>
            <div className="font-semibold text-white">{it.title}</div>
            <div className="text-xs text-[#8E9196] mt-0.5">{it.body}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function FeatureGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2.5 not-prose mt-3">
      {items.map((it, i) => (
        <div key={i} className="border border-white/10 rounded-sm p-3 bg-[#0a0a0a] hover:border-[#00F0FF]/40 transition-colors">
          <it.icon className="w-4 h-4 text-[#00F0FF] mb-2" strokeWidth={1.5} />
          <div className="font-semibold text-white text-sm">{it.title}</div>
          <div className="text-xs text-[#8E9196] mt-1 leading-relaxed">{it.body}</div>
        </div>
      ))}
    </div>
  );
}

function Callout({ tone = "cyan", title, children }) {
  const color = tone === "amber" ? "#FFCC00" : tone === "red" ? "#FF3B30" : "#00F0FF";
  return (
    <div className="not-prose border rounded-sm p-3 mt-3"
         style={{ borderColor: color + "60", backgroundColor: color + "0F" }}>
      <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color }}>
        {title}
      </div>
      <div className="text-xs text-white leading-relaxed">{children}</div>
    </div>
  );
}
